import { useState } from 'react'
import { Alert, Button, Checkbox, Message, Space, Tag, Typography } from '@arco-design/web-react'
import SettingSectionCard from '../SettingSectionCard'
import { confirmPaymentComplianceApi } from '@/api/option'
import type { OptionsAPI } from '../useOptions'

const COMPLIANCE_KEYS = [
  'payment_setting.compliance_confirmed',
  'payment_setting.compliance_terms_version',
  'payment_setting.compliance_confirmed_at',
  'payment_setting.compliance_confirmed_by',
  'payment_setting.compliance_confirmed_ip',
]

/** 与后端 operation_setting.CurrentComplianceTermsVersion 保持一致 */
const CURRENT_TERMS_VERSION = 'v1'

const formatTs = (raw: string): string => {
  const ts = Number(raw)
  if (!Number.isFinite(ts) || ts <= 0) return '-'
  return new Date(ts * 1000).toLocaleString()
}

/**
 * 支付合规确认。
 *
 * 后端会把「充值 / 兑换码 / 订阅 / 邀请奖励」全部拦住，直到管理员在这里确认条款；
 * 这几个字段不能通过普通设置接口修改，只能走 POST /api/option/payment_compliance。
 */
export default function PaymentComplianceCard({ options }: { options: OptionsAPI }) {
  const { get } = options
  const [checked, setChecked] = useState(false)
  const [loading, setLoading] = useState(false)

  const confirmed = get('payment_setting.compliance_confirmed') === 'true'
  const termsVersion = get('payment_setting.compliance_terms_version')
  const versionOk = termsVersion === CURRENT_TERMS_VERSION
  const active = confirmed && versionOk

  const submit = async () => {
    if (!checked) {
      Message.warning('请先阅读并勾选合规声明')
      return
    }
    setLoading(true)
    try {
      await confirmPaymentComplianceApi()
      Message.success('已确认合规声明，充值与订阅功能可以启用了')
      setChecked(false)
      await options.reload()
    } catch (err) {
      Message.error(err instanceof Error ? err.message : '确认失败')
    } finally {
      setLoading(false)
    }
  }

  return (
    <SettingSectionCard
      title="支付合规确认"
      subtitle="未确认前，充值、兑换码、订阅与邀请奖励都会被后端拒绝"
      keys={[...COMPLIANCE_KEYS]}
      options={options}
      hideSave
    >
      <Alert
        type={active ? 'success' : 'warning'}
        className="mb-3"
        content={
          active
            ? `已确认（条款版本 ${termsVersion}，${formatTs(
                get('payment_setting.compliance_confirmed_at')
              )}，操作人 ID ${get('payment_setting.compliance_confirmed_by') || '-'}）`
            : confirmed && !versionOk
              ? `已确认过，但条款版本已更新（当前记录 ${termsVersion || '空'}，需要 ${CURRENT_TERMS_VERSION}），请重新确认`
              : '尚未确认：充值与订阅相关的后端接口现在都会返回"需管理员确认合规条款"'
        }
      />
      <Typography.Paragraph className="text-xs text-[var(--console-text-muted)]">
        确认表示你已核对本平台开展支付/兑换/订阅业务的合规要求，并自行承担相应责任。
        {active ? (
          <Space className="ml-2">
            <Tag color="green">已生效</Tag>
          </Space>
        ) : null}
      </Typography.Paragraph>
      <Space direction="vertical" size={8}>
        <Checkbox checked={checked} onChange={setChecked}>
          我已阅读并同意承担上述合规责任
        </Checkbox>
        <Button type="primary" loading={loading} onClick={submit}>
          {active ? '重新确认合规声明' : '确认合规声明'}
        </Button>
      </Space>
    </SettingSectionCard>
  )
}
